import React, { useCallback, useEffect, useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';

const PRESET_COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#f43f5e',
  '#f97316', '#eab308', '#22c55e', '#14b8a6',
  '#06b6d4', '#3b82f6', '#64748b', '#78716c',
];

function ColorPicker({ value, onChange }) {
  const isPreset = value && PRESET_COLORS.includes(value);
  const isCustom = value && !isPreset;

  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-2">Color</label>
      <div className="flex flex-wrap items-center gap-2">
        {PRESET_COLORS.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => onChange(c)}
            className={`h-7 w-7 rounded-full border-2 transition ${value === c ? 'border-slate-800 scale-110' : 'border-transparent hover:scale-105'}`}
            style={{ backgroundColor: c }}
            title={c}
          />
        ))}

        {/* Custom colour picker */}
        <label
          className={`relative h-7 w-7 cursor-pointer rounded-full border-2 transition hover:scale-105 overflow-hidden ${isCustom ? 'border-slate-800 scale-110' : 'border-slate-300'}`}
          title="Custom colour"
          style={isCustom ? { backgroundColor: value } : { background: 'conic-gradient(red, yellow, lime, cyan, blue, magenta, red)' }}
        >
          <input
            type="color"
            value={value && /^#[0-9a-f]{6}$/i.test(value) ? value : '#6366f1'}
            onChange={(e) => onChange(e.target.value)}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          />
        </label>

        {/* Clear */}
        <button
          type="button"
          onClick={() => onChange(null)}
          className={`h-7 w-7 rounded-full border-2 transition flex items-center justify-center bg-slate-100 ${!value ? 'border-slate-800 scale-110' : 'border-slate-300 hover:scale-105'}`}
          title="No color"
        >
          <span className="text-slate-400 text-[10px]">✕</span>
        </button>
      </div>

      {value && (
        <div className="mt-2 flex items-center gap-2">
          <span className="h-4 w-4 rounded border border-black/10" style={{ backgroundColor: value }} />
          <span className="font-mono text-xs text-slate-500">{value}</span>
        </div>
      )}
    </div>
  );
}

function CategoryModal({ category, onClose, onSave }) {
  const isEdit = Boolean(category?.id);
  const [name, setName] = useState(category?.name || '');
  const [description, setDescription] = useState(category?.description || '');
  const [color, setColor] = useState(category?.color || null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) { setError('Name is required'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = { name: trimmed, description: description.trim(), color };
      let result;
      if (isEdit) {
        const resp = await axiosInstance.patch(`/tickets/admin/categories/${category.id}`, payload);
        result = resp.data;
      } else {
        const resp = await axiosInstance.post('/tickets/admin/categories', payload);
        result = resp.data;
      }
      onSave(result, isEdit);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h2 className="text-base font-semibold text-slate-800">
            {isEdit ? 'Edit Category' : 'New Category'}
          </h2>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Assignment Deadline"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
              maxLength={50}
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional short description shown to students"
              rows={3}
              className="w-full resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            />
          </div>
          <ColorPicker value={color} onChange={setColor} />
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
            >
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeleteConfirm({ category, onClose, onConfirm, loading }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-sm rounded-2xl bg-white shadow-2xl">
        <div className="px-6 py-5">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
          </div>
          <h3 className="text-center text-base font-semibold text-slate-800">Delete "{category.name}"?</h3>
          <p className="mt-2 text-center text-sm text-slate-500">
            If this category has existing tickets it will be deactivated instead of permanently deleted.
          </p>
        </div>
        <div className="flex gap-3 border-t border-slate-200 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60"
          >
            {loading ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TicketCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const loadCategories = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await axiosInstance.get('/tickets/admin/categories');
      setCategories(resp.data || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  const handleSave = (saved, isEdit) => {
    if (isEdit) {
      setCategories((prev) => prev.map((c) => c.id === saved.id ? saved : c));
    } else {
      setCategories((prev) => [...prev, saved].sort((a, b) => a.name.localeCompare(b.name)));
    }
    setModal(null);
  };

  const handleToggleActive = async (cat) => {
    setTogglingId(cat.id);
    try {
      const resp = await axiosInstance.patch(`/tickets/admin/categories/${cat.id}`, { is_active: !cat.is_active });
      setCategories((prev) => prev.map((c) => c.id === cat.id ? resp.data : c));
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to update');
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const resp = await axiosInstance.delete(`/tickets/admin/categories/${deleteTarget.id}`);
      if (resp.data?.deleted) {
        setCategories((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      } else {
        setCategories((prev) => prev.map((c) => c.id === deleteTarget.id ? { ...c, is_active: 0 } : c));
      }
      setDeleteTarget(null);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <DashboardLayout title="Ticket Categories">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Ticket Categories</h1>
          <p className="text-sm text-slate-500">Manage the categories available in the student ticket submission form.</p>
        </div>
        <button
          type="button"
          onClick={() => setModal({ mode: 'add' })}
          className="flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Category
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 w-8">#</th>
              <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Name</th>
              <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Description</th>
              <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 w-28">Color</th>
              <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 w-28">Status</th>
              <th className="px-5 py-3.5 text-right text-xs font-semibold uppercase tracking-wide text-slate-500 w-36">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-5 py-12 text-center text-sm text-slate-400">Loading…</td>
              </tr>
            ) : categories.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-5 py-12 text-center text-sm text-slate-400">
                  No categories yet — click <span className="font-medium text-indigo-600">Add Category</span> to create one.
                </td>
              </tr>
            ) : (
              categories.map((cat, idx) => (
                <tr key={cat.id} className={`transition hover:bg-slate-50 ${!cat.is_active ? 'opacity-50' : ''}`}>
                  <td className="px-5 py-3.5 text-sm text-slate-400">{idx + 1}</td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2.5">
                      <span
                        className="h-3 w-3 flex-shrink-0 rounded-full border border-black/10"
                        style={{ backgroundColor: cat.color || '#cbd5e1' }}
                      />
                      <span className="text-sm font-medium text-slate-800">{cat.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-sm text-slate-500 max-w-xs">
                    <span className="line-clamp-2">{cat.description || <span className="italic text-slate-300">—</span>}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    {cat.color ? (
                      <div className="flex items-center gap-2">
                        <span
                          className="h-5 w-5 rounded border border-black/10"
                          style={{ backgroundColor: cat.color }}
                        />
                        <span className="font-mono text-xs text-slate-500">{cat.color}</span>
                      </div>
                    ) : (
                      <span className="text-xs italic text-slate-300">None</span>
                    )}
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                      cat.is_active
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                        : 'bg-slate-100 text-slate-500 border border-slate-200'
                    }`}>
                      {cat.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-2">
                      {/* Edit */}
                      <button
                        type="button"
                        onClick={() => setModal({ mode: 'edit', category: cat })}
                        className="rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                        title="Edit"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>

                      {/* Toggle active */}
                      <button
                        type="button"
                        onClick={() => handleToggleActive(cat)}
                        disabled={togglingId === cat.id}
                        title={cat.is_active ? 'Deactivate' : 'Activate'}
                        className={`rounded-lg border p-1.5 transition disabled:opacity-40 ${
                          cat.is_active
                            ? 'border-amber-200 text-amber-600 hover:bg-amber-50'
                            : 'border-emerald-200 text-emerald-600 hover:bg-emerald-50'
                        }`}
                      >
                        {togglingId === cat.id ? (
                          <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                          </svg>
                        ) : cat.is_active ? (
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                          </svg>
                        ) : (
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        )}
                      </button>

                      {/* Delete */}
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(cat)}
                        title="Delete"
                        className="rounded-lg border border-red-200 p-1.5 text-red-500 hover:bg-red-50"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Footer count */}
        {!loading && categories.length > 0 && (
          <div className="border-t border-slate-100 bg-slate-50 px-5 py-2.5 text-xs text-slate-400">
            {categories.length} {categories.length === 1 ? 'category' : 'categories'} &nbsp;·&nbsp;
            {categories.filter((c) => c.is_active).length} active,&nbsp;
            {categories.filter((c) => !c.is_active).length} inactive
          </div>
        )}
      </div>

      {modal && (
        <CategoryModal
          category={modal.mode === 'edit' ? modal.category : null}
          onClose={() => setModal(null)}
          onSave={handleSave}
        />
      )}

      {deleteTarget && (
        <DeleteConfirm
          category={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          loading={deleting}
        />
      )}
    </DashboardLayout>
  );
}
